import { DashboardContextType } from "@/components/dashboard/DashboardContext";
import { EditExternalAccountModal } from "@/components/modals/EditExternalAccountModal";
import { AddBankAccountWizard, BankAccountsCard, CryptoWalletsCard } from "@/components/send";
import { ErrorBoundary, SectionErrorBoundary } from "@/components/ui/ErrorBoundary";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/typography";
import { ExternalAccount, useExternalAccounts } from "@/features/user/wallet/hooks/useWallet";
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Page, PageHeader, PageContent } from "@supabank/ui";
import { Building2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

/**
 * External Accounts Page - Bank accounts & Crypto wallets for withdrawals
 * 
 * CANON v1.4.5 Design Contract:
 * - Page component с data-ux-contract="canon"
 * - Hero Cards (Liquid Glass style) — паттерн BalanceCard
 * - Кнопки действий ВНУТРИ карточек (не в Page.Header)
 * - Skeleton-first loading (CANON 5.3/5.4)
 * - Motion: 240ms canonical
 * - Touch targets ≥44px
 * 
 * Displays:
 * - Bank Accounts (External accounts for fiat withdrawals)
 * - Crypto Wallets (External wallets for crypto withdrawals)
 * 
 * @see PRD-WALLET-HUB
 */
export default function SendPage() {
  const context = useOutletContext<DashboardContextType>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isLoading = context.loading || false;
  const [selectedAccount, setSelectedAccount] = useState<ExternalAccount | null>(null);
  const [isDetailModalOpen, setDetailModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isBankWizardOpen, setBankWizardOpen] = useState(false);

  // Bank accounts (External accounts for fiat withdrawals)
  const { data: externalAccounts, isLoading: isLoadingAccounts, refetch: refetchAccounts } = useExternalAccounts();

  // Auto-open wizard from URL param (for legacy /add-bank-account redirects)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add-bank-account') {
      setBankWizardOpen(true);
      // Clean up URL after opening wizard
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Refresh handler for bank accounts only (CryptoWalletsCard manages its own data)
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetchAccounts?.();
    } finally {
      setTimeout(() => setIsRefreshing(false), 300);
    }
  }, [refetchAccounts]);

  const handleAddBankAccount = () => {
    setBankWizardOpen(true);
  };

  const handleAccountClick = (account: ExternalAccount) => {
    try {
      // Strict validation before opening modal
      if (!account || typeof account !== 'object' || !account.id) {
        console.error('[SendPage] Invalid account data:', account);
        toast.error('Invalid account data');
        return;
      }

      // Additional validation: ensure required fields exist
      if (!account.status || typeof account.status !== 'string') {
        console.error('[SendPage] Account missing status:', account);
        toast.error('Account data is incomplete');
        return;
      }

      setSelectedAccount(account);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('[SendPage] Error opening account details:', error);
      toast.error('Failed to open account details');
    }
  };

  return (
    <>
      {/* Add Bank Account Wizard - Sheet */}
      <AddBankAccountWizard
        open={isBankWizardOpen}
        onClose={() => setBankWizardOpen(false)}
        onSuccess={() => refetchAccounts?.()}
      />

      {isDetailModalOpen && selectedAccount && (
        <ErrorBoundary level="component" fallback={
          <div className={cn("p-4 bg-destructive/5 border border-destructive/20 rounded", semanticTokens.typography.bodySmall, semanticTokens.danger.fg)}>
            Failed to load account details. Please try again.
          </div>
        }>
          <EditExternalAccountModal
            open={isDetailModalOpen}
            onClose={() => {
              setDetailModalOpen(false);
              setSelectedAccount(null);
            }}
            account={selectedAccount}
            onAccountUpdated={() => {
              // Invalidate queries to refresh the list
            }}
          />
        </ErrorBoundary>
      )}
      <Page data-ux-contract="canon" className={semanticTokens.spacing.lg}>
        {/* Header */}
        <PageHeader
          icon={Building2}
          title="External Accounts"
          showBackButton={true}
          onBack={() => navigate('/dashboard')}
        />

        {/* Description */}
        <div className={cn("px-1 -mt-2 mb-4", semanticTokens.spacing.lg.replace('p-', 'px-'))}>
          <Text variant="sm" color="muted">
            Linked accounts and wallets for receiving withdrawals directly — without going through your SupaWallet.
          </Text>
        </div>

        {/* Content - Revolut-style: Hero Cards с действиями внутри */}
        <PageContent>
          {isLoading ? (
            // Skeleton loading - CANON 5.3/5.4: Shape-matching skeletons
            <div
              className={cn("space-y-8 lg:space-y-12", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}
              role="status"
              aria-live="polite"
              aria-label="Loading send page"
              data-testid="send-page-skeleton"
            >
              {/* Hero Card Skeleton */}
              <div className={cn(
                "rounded-[28px] p-6",
                "bg-gradient-to-br from-white/90 via-white/70 to-white/90",
                "border border-white/20"
              )}>
                <Skeleton className={cn("h-8 w-36 mb-4", semanticTokens.radius.sm)} />
                <Skeleton className={cn("h-4 w-full max-w-md mb-6", semanticTokens.radius.sm)} />
                <div className={cn("space-y-3", semanticTokens.spacing.sm.replace('p-', 'space-y-'))}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className={cn("h-20 w-full", semanticTokens.radius.lg)} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className={cn("space-y-8 lg:space-y-12", semanticTokens.spacing.lg.replace('p-', 'space-y-'))}>
              {/* Bank Accounts Card - Hero Liquid Glass */}
              <SectionErrorBoundary>
                <BankAccountsCard
                  accounts={externalAccounts || []}
                  loading={isLoadingAccounts || isRefreshing}
                  isRefreshing={isRefreshing}
                  onAddAccount={handleAddBankAccount}
                  onRefresh={handleRefresh}
                  onAccountClick={handleAccountClick}
                />
              </SectionErrorBoundary>

              {/* Crypto Wallets Card - Self-contained with useSavedAddresses */}
              <SectionErrorBoundary>
                <CryptoWalletsCard />
              </SectionErrorBoundary>
            </div>
          )}
        </PageContent>
      </Page>
    </>
  );
}
